/* eslint-disable camelcase */
import React from 'react'
import { Divider, Grid } from '@mui/material'
import Checkbox from 'components/forms/Checkbox'
import Select from 'components/forms/Select'
import SimpleTextArea from 'components/forms/SimpleTextArea'
import DtoCMSQuestion from 'events/dto/survey/DtoCMSQuestion'
import AddPicture from 'installation/components/campaign/components/AddPicture'
import VisitPicture from 'installation/components/campaign/components/VisitPicture'
import DtoAnswer from 'installation/components/campaign/dto/DtoAnswer'
import DtoAnswerDocument from 'installation/components/campaign/dto/DtoAnswerDocument'
import DtoVisit from 'installation/components/campaign/dto/DtoVisit'
import { DANGER_ELEMENT_TYPE } from 'installation/constants/InstallationConstants'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import useSandreList from 'utils/customHook/useSandreList'
import ToggleButtonGroup from 'components/buttons/ToggleButtonGroup'
import { mediumGrey, PASTEL_COLOR } from 'utils/constants/ColorTheme'
import { ANSWER_STATE } from 'pgsse/constant/PgsseConstants'

const Question = ({
    question,
    dataForm,
    onChange,
    readMode,
}) => {
    const answer = dataForm.link_answers.find(la => la.idQuestion === question.idQuestion) || {}
    const answerWithIndexs = answer.link_documents && {
        ...answer,
        link_documents: answer.link_documents.map((d, index) => ({ ...d, index })),
    } || {}

    const updateQuestion = (newAnswer) => {
        const newAnswers = [
            ...dataForm.link_answers.filter(la => !(la.idQuestion === question.idQuestion && question.idParagraph === la.idParagraph)),
            newAnswer,
        ].filter(d => d?.idParagraph)
        onChange({ link_answers: newAnswers })
    }

    const onChangeQuestion = (value) => {
        const newObjectAnswer = new DtoAnswer({
            recipientCode: dataForm.code,
            idCampaign: dataForm.idCampaign,
            recipientType: DANGER_ELEMENT_TYPE.INSTALLATION,
            idSurvey: dataForm.idSurvey,
            idVisit: dataForm.idForm,
            idQuestion: question.idQuestion,
            idParagraph: question.idParagraph,
            ...answerWithIndexs,
            ...value,
        })
        const newAnswer = (newObjectAnswer.answerBool
            || newObjectAnswer.answerDate
            || newObjectAnswer.answerLong
            || newObjectAnswer.answerMultiChoice
            || newObjectAnswer.answerText
            || newObjectAnswer?.link_documents.length) ? newObjectAnswer : undefined

        updateQuestion(newAnswer)
    }

    const addNewDocument = (document) => {
        const newLinkDocuments = [
            ...(answerWithIndexs.link_documents || []),
            document,
        ]
        onChangeQuestion({ link_documents: newLinkDocuments })
    }

    const onChangeDocument = (document, value) => {
        const newLinkDocuments = answerWithIndexs.link_documents.map(d => {
            if (d.index === document.index) {
                return new DtoAnswerDocument({ ...document, ...value })
            }
            return d
        })
        onChangeQuestion({ link_documents: newLinkDocuments })
    }

    const onDeleteDocument = (document) => {
        onChangeQuestion({ link_documents: [...answerWithIndexs.link_documents.filter(d => d.index !== document.index)] })
    }

    const displayPictures = answerWithIndexs.link_documents && answerWithIndexs.link_documents.map(doc => {
        return (
            <VisitPicture
                key={doc.docName}
                doc={doc}
                onDeleteDocument={() => onDeleteDocument(doc)}
                onChangeDocument={value => onChangeDocument(doc, { description: value })}
                readMode={readMode}
                col={4}
            />
        )
    })

    const amc = answerWithIndexs.answerMultiChoice // answerWithIndexs.answerMultiChoice

    const sandreList = useSandreList(question.sandreField)

    return (
        <Grid container spacing={1}>
            <Grid container size={{ xs: 12, md: 8 }} direction='column'>
                <Grid size={12} container>
                    <Grid size={7}>
                        <h5>{question.description}</h5>
                    </Grid>
                    <Grid container size={5} justifyContent='flex-end' alignItems='center'>
                        <ToggleButtonGroup
                            buttons={[
                                { value: ANSWER_STATE.YES, color: PASTEL_COLOR.green, name: i18n.yes },
                                { value: ANSWER_STATE.NO, color: PASTEL_COLOR.red, name: i18n.no },
                                { value: ANSWER_STATE.WITHOUTOBJECT, color: mediumGrey, name: i18n.withoutObject },
                            ]}
                            value={amc}
                            onChange={(value) => onChangeQuestion({ answerMultiChoice: value })}
                            readMode={readMode}
                        />
                    </Grid>
                </Grid>
                <Grid size={12} container>
                    <Grid size={6}>
                        {question.sandreField && (
                            <Select
                                obligatory
                                options={sandreList}
                                keyValue='code'
                                keyLabel='name'
                                label={i18n.state}
                                value={answerWithIndexs.answerLong}
                                onChange={v => onChangeQuestion({ answerLong: v })}
                                disabled={readMode}
                            />
                        )}
                    </Grid>
                    <Grid container size={6} justifyContent='flex-end' alignItems='center'>
                        <Grid>
                            <Checkbox
                                col={12}
                                label={i18n.isStateProblem}
                                checked={answerWithIndexs.answerBool}
                                onChange={v => onChangeQuestion({ answerBool: v })}
                                disabled={readMode}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                {question.inputComment && (
                    <Grid size={12}>
                        <SimpleTextArea
                            title={i18n.comment}
                            value={answerWithIndexs.answerText}
                            {...readMode}
                            onChange={v => onChangeQuestion({ answerText: v })}
                            disabled={readMode}
                            readMode={readMode}
                        />
                    </Grid>
                )}
            </Grid>
            <Grid container size={{ xs: 12, md: 4 }} justifyContent='space-evenly'>
                <Grid container spacing={2}>
                    {displayPictures}
                    {!readMode && (
                        <AddPicture
                            onUploadFile={newFileName => {
                                addNewDocument({
                                    idSurvey: dataForm.idSurvey,
                                    idInstallation: dataForm.idInstallation,
                                    idLinkedQuestion: question.idQuestion,
                                    docName: newFileName,
                                    idVisit: dataForm.idVisit,
                                    idCampaign: dataForm.idCampaign,
                                })
                            }}
                        />
                    )}
                </Grid>
            </Grid>
            <Grid size={12} sx={{ marginBottom: '10px' }}>
                <Divider />
            </Grid>
        </Grid>
    )
}

Question.propTypes = {
    question: PropTypes.instanceOf(DtoCMSQuestion),
    dataForm: PropTypes.instanceOf(DtoVisit),
    onChange: PropTypes.func,
    uploadFile: PropTypes.func,
    readMode: PropTypes.bool,
}

export default Question